import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useActiveOrg } from "@/hooks/use-active-org";
import { useOrgQuota } from "@/hooks/use-org-quota";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowUpRightSquare,
  Check,
  ChevronsUpDown,
  CreditCard,
  LogOut,
  Plus,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { CreateWorkspaceDialog } from "@/components/workspaces/create-workspace-dialog";
import { useClerk } from "@clerk/clerk-react";
import { getErrorMessage } from "@/lib/api-error";

export function WorkspaceSwitcher() {
  const { isMobile, setOpenMobile } = useSidebar();
  const queryClient = useQueryClient();
  const { signOut } = useClerk();
  const navigate = useNavigate();

  const { activeWorkspace, setActiveWorkspaceId, workspaces } =
    useWorkspaceStore();
  const { orgId, isOrgAdmin } = useActiveOrg();
  const { quota } = useOrgQuota();

  const [isSwitching, setIsSwitching] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  if (!activeWorkspace) {
    return null;
  }

  const org = activeWorkspace.affiliated_company;
  // Fail closed: only surface the create affordance once quota has loaded and
  // confirms headroom. While the query is loading (e.g. after a hard refresh
  // wipes the cache) quota is undefined — gating on "not at cap" would render
  // the item during that window and let a capped user create over the limit.
  const canCreateWorkspace =
    !!quota && quota.workspaces_used < quota.workspaces_cap;

  const handleWorkspaceChange = async (workspace: typeof activeWorkspace) => {
    if (isSwitching) return;
    setIsSwitching(true);
    if (isMobile) setOpenMobile(false);

    try {
      setActiveWorkspaceId(workspace._id);
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            queryKey.includes("deal") ||
            queryKey.includes("deals") ||
            queryKey.includes("workspace") ||
            queryKey.includes("reports")
          );
        },
      });
      // Full reload — switching via SPA navigation has surfaced stale-data
      // bugs in components that don't react to active-workspace changes.
      // The reload tears down every cache and rebuilds with the new id.
      window.location.href = `/${workspace._id}/dashboard`;
    } catch (error) {
      console.error("Failed to change workspace:", error);
      toast.error(getErrorMessage(error, "Couldn't switch workspace"));
      setIsSwitching(false);
    }
    // No `finally` — the success path triggers a full reload, so the
    // component unmounts before any cleanup matters; only the error branch
    // resets isSwitching above.
  };

  const handleNavigate = (path: string) => {
    if (isMobile) setOpenMobile(false);
    navigate(path);
  };

  return (
    <SidebarMenu className="workspace-switcher">
      <SidebarMenuItem>
        {/*
         * `modal={false}` keeps Radix from applying its body scroll-lock and
         * `pointer-events: none` shim while open. Items in this menu trigger
         * SPA navigation, which unmounts the dropdown mid-close — the cleanup
         * never runs and the lock leaks onto the next page (on mobile, the
         * residual overflow lock prevents scrolling until refresh).
         */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="default"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground px-1 py-1 group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:!justify-center"
              disabled={isSwitching}
              data-tour="workspace-switcher"
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-sidebar-primary text-sidebar-primary-foreground">
                <span className="text-sm font-semibold leading-none">
                  {activeWorkspace.opportunity_name.charAt(0)}
                </span>
              </div>
              <span className="flex-1 truncate text-left text-sm font-semibold text-sidebar-foreground group-data-[collapsible=icon]:hidden">
                {isSwitching
                  ? "Switching..."
                  : activeWorkspace.opportunity_name}
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-64 overflow-hidden rounded-lg p-0"
            align="start"
            side="bottom"
            sideOffset={4}
          >
            {/* Workspace section */}
            <div className="p-1">
              <DropdownMenuLabel className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Workspace
              </DropdownMenuLabel>
              {workspaces.slice(0, 7).map((workspace) => (
                <DropdownMenuItem
                  key={workspace._id}
                  data-workspace-id={workspace._id}
                  onClick={() => handleWorkspaceChange(workspace)}
                  className="flex items-center gap-2.5 rounded px-2 py-2"
                >
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-sidebar-primary text-sidebar-primary-foreground">
                    <span className="text-xs font-semibold leading-none">
                      {workspace.opportunity_name.charAt(0)}
                    </span>
                  </div>
                  <span className="flex-1 truncate text-sm">
                    {workspace.opportunity_name}
                  </span>
                  {workspace.role && (
                    <span className="shrink-0 text-[10px] font-medium capitalize text-muted-foreground">
                      {workspace.role}
                    </span>
                  )}
                  <Check
                    className={`h-3.5 w-3.5 shrink-0 text-muted-foreground ${
                      activeWorkspace._id === workspace._id
                        ? "visible"
                        : "invisible"
                    }`}
                  />
                </DropdownMenuItem>
              ))}
            </div>

            <DropdownMenuSeparator className="my-0" />

            {/* Organization section — display-only, no switcher */}
            {org?.company_name && (
              <>
                <div className="p-1">
                  <DropdownMenuLabel className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Organization
                  </DropdownMenuLabel>
                  <div className="flex items-center gap-2.5 rounded px-2 py-2">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-sidebar-primary text-sidebar-primary-foreground">
                      <span className="text-xs font-semibold leading-none">
                        {org.company_name.charAt(0)}
                      </span>
                    </div>
                    <span className="flex-1 truncate text-sm">
                      {org.company_name}
                    </span>
                  </div>
                </div>

                <DropdownMenuSeparator className="my-0" />
              </>
            )}

            {/* Billing section — admin only, org-scoped */}
            {isOrgAdmin && orgId && (
              <>
                <div className="p-1">
                  <DropdownMenuItem
                    className="flex items-center gap-2.5 rounded px-2 py-2 text-sm"
                    onClick={() =>
                      handleNavigate(`/org/${orgId}/settings/billing`)
                    }
                  >
                    <CreditCard className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span>Billing &amp; usage</span>
                  </DropdownMenuItem>
                </div>

                <DropdownMenuSeparator className="my-0" />
              </>
            )}

            {/* Actions */}
            <div className="p-1">
              {canCreateWorkspace && (
                <DropdownMenuItem
                  className="flex items-center gap-2.5 rounded px-2 py-2 text-sm text-muted-foreground"
                  onClick={() => setShowCreateDialog(true)}
                >
                  <Plus className="h-4 w-4" />
                  Create or join workspace
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="flex items-center gap-2.5 rounded px-2 py-2 text-sm text-muted-foreground"
                onClick={() => handleNavigate("/workspaces")}
              >
                <ArrowUpRightSquare className="h-4 w-4" />
                View all workspaces
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex items-center gap-2.5 rounded px-2 py-2 text-sm text-muted-foreground"
                onClick={() => signOut({ redirectUrl: "/" })}
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>

      <CreateWorkspaceDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </SidebarMenu>
  );
}
